import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  styled
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';
import intl from 'components/i18n/ReactIntlWrapper';
import Spinner from 'components/Spinner';
import { TimeOffType } from 'axiosApi/models/time-off-type';

export type TimeOffStock = {
  id: number;
  idEmployeeRegimeTimeOffType: number;
  validFrom: string;
  validUntil: string;
  totalStock: number;
  availableStock: number;
  name: string | null;
};

export type EmployeeRegimeTimeOffType = {
  id: number;
  idTimeOffType: number;
  idEmployeeTimeOffRegime: number;
  timeOffType: TimeOffType ;

  isActive: boolean;
};

interface TimeOffStockCarrouselProps {
  timeOffStocks: any[];
  selectedTimeOffType?: number;
  employeeRegimeTimeOffTypes?: any[];
  loading?: boolean;
}

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  padding: theme.spacing(2, 2),
}));

const CarouselContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(0, 4),
  width: 'fit-content',
  transition: 'opacity 0.2s ease-in-out',
}));

const StockCard = styled(Box)<{ highlighted?: boolean }>(({ theme, highlighted }) => ({
  minWidth: 280,
  padding: theme.spacing(2),
  backgroundColor: highlighted ? "#e3f2fd" : theme.palette.grey[100],
  borderRadius: theme.spacing(2),
  border: `2px solid ${highlighted ? "#90caf9" : theme.palette.grey[300]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: highlighted ? "#e3f2fd" : theme.palette.grey[200],
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: theme.shadows[2],
  zIndex: 10,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[100],
    opacity: 0.5,
  },
}));

const StockChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.grey[700],
  color: theme.palette.common.white,
  fontSize: '0.75rem',
  height: 24,
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1),
  },
}));

const TimeOffStockCarrousel: React.FC<TimeOffStockCarrouselProps> = ({ timeOffStocks, selectedTimeOffType, employeeRegimeTimeOffTypes, loading = false }) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(4);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const cardWidth = 280;
  const cardGap = 16;
  const cardStep = cardWidth + cardGap;

  const needsInfiniteScroll = timeOffStocks && timeOffStocks.length > cardsPerView;

  const originalLength = timeOffStocks?.length || 0;

  const maxIndex = needsInfiniteScroll
    ? Infinity
    : Math.max(0, originalLength - cardsPerView);

  const getDisplayCards = () => {
    if (!timeOffStocks || timeOffStocks.length === 0) return [];
    
    const enrichedStocks = timeOffStocks.map((stock) => ({
      ...stock,
      timeOffType: employeeRegimeTimeOffTypes?.find(type => type.id === stock.idEmployeeRegimeTimeOffType)?.timeOffType
    }));
    
    if (!needsInfiniteScroll) {
      return enrichedStocks;
    }
    return [...enrichedStocks, ...enrichedStocks, ...enrichedStocks];
  };

  const displayCards = getDisplayCards();

  const normalizedIndex = needsInfiniteScroll ?
    (currentIndex % originalLength + originalLength) % originalLength + originalLength :
    currentIndex;

  const calculateCardsPerView = () => {
    if (!carouselRef.current) return 4;

    const containerWidth = carouselRef.current.offsetWidth;
    const navigationButtonWidth = 48;
    const containerPadding = 32 * 2;

    const availableWidth = containerWidth - (navigationButtonWidth * 2) - containerPadding;
    const calculatedCardsPerView = Math.floor(availableWidth / cardStep);

    return Math.max(1, calculatedCardsPerView);
  };

  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = calculateCardsPerView();
      setCardsPerView(newCardsPerView);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [cardStep]);

  useEffect(() => {
    if (needsInfiniteScroll && originalLength > 0) {
      setCurrentIndex(originalLength);
    } else {
      setCurrentIndex(0);
    }
  }, [timeOffStocks, needsInfiniteScroll, originalLength]);

  useEffect(() => {
    if (!needsInfiniteScroll) {
      const newMaxIndex = timeOffStocks && timeOffStocks.length > 0
        ? Math.max(0, timeOffStocks.length - cardsPerView)
        : 0;
      if (currentIndex > newMaxIndex) {
        setCurrentIndex(newMaxIndex);
      }
    }
  }, [cardsPerView, timeOffStocks, currentIndex, needsInfiniteScroll]);

  useEffect(() => {
    if (needsInfiniteScroll && originalLength > 0) {
      if (currentIndex >= originalLength * 2.5 || currentIndex < originalLength * 0.5) {
        const equivalentIndex = ((currentIndex % originalLength) + originalLength) % originalLength + originalLength;
        setCurrentIndex(equivalentIndex);
      }
    }
  }, [currentIndex, needsInfiniteScroll, originalLength]);


  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (needsInfiniteScroll) {
          return prev - 1;
        } else {
          return Math.max(0, prev - 1);
        }
      });
      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    }, 100);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (needsInfiniteScroll) {
          return prev + 1;
        } else {
          return Math.min(maxIndex, prev + 1);
        }
      });
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 100);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const getStockTags = (stock: TimeOffStock) => {
    const tags = [];

    const matchingType = employeeRegimeTimeOffTypes?.find(
      type => type.id === stock.idEmployeeRegimeTimeOffType
    );

    if (matchingType) {
      const timeOffType = matchingType?.timeOffType;

      if (timeOffType?.isCumulative) {
        tags.push(intl.get('timeOff.perks.cumulative'));
      }
      if (timeOffType?.isBillable) {
        tags.push(intl.get('timeOff.perks.billable'));
      }
      if (timeOffType?.allowNegativeBalance) {
        tags.push(intl.get('timeOff.perks.negativeBalance'));
      }
    }
    return tags;
  };

  return (
    <>
    { loading ? <Spinner /> : timeOffStocks && timeOffStocks.length > 0 && (
    <CarouselContainer ref={carouselRef}>
      {needsInfiniteScroll && (
        <NavigationButton
          onClick={handlePrevious}
          disabled={isTransitioning}
          sx={{
            left: theme.spacing(2),
            opacity: isTransitioning ? 0.6 : 1,
          }}
        >
          <ChevronLeftIcon />
        </NavigationButton>
      )}

        <CarouselContent
          sx={{
            transform: `translateX(-${normalizedIndex * cardStep}px)`,
            willChange: 'transform',
            opacity: isTransitioning ? 0.3 : 1,
          }}
        >
        {displayCards.map((stock, index) => (
          <StockCard
            key={`${stock.id}-${index}`}
            highlighted={selectedTimeOffType === stock.id}
          >
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                lineHeight: 1,
              }}
            >
              {stock?.availableStock}/{stock?.totalStock}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
              }}
            >
              {stock?.name}
            </Typography>
            {stock?.name !== stock?.timeOffType?.name && (
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                }}
              >
                {stock?.timeOffType?.labelKey ? intl.get(stock?.timeOffType?.labelKey) : stock?.timeOffType?.name}
              </Typography>
            )}
            <Typography 
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              {formatDate(stock?.validFrom)} - {formatDate(stock?.validUntil || '')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {getStockTags(stock)?.map((tag, tagIndex) => (
                <StockChip
                  key={tagIndex}
                  label={tag}
                  size="small"
                />
              ))}
            </Box>
          </StockCard>
        ))}
      </CarouselContent>

      {needsInfiniteScroll && (
        <NavigationButton
          onClick={handleNext}
          disabled={isTransitioning || !maxIndex}
          sx={{
            right: theme.spacing(2),
            opacity: isTransitioning ? 0.6 : 1,
          }}
        >
          <ChevronRightIcon />
        </NavigationButton>
      )}
    </CarouselContainer>
      )}</>
  );
};

export default TimeOffStockCarrousel;
